import { makeStyles, Theme } from "@material-ui/core";
import "quill/dist/quill.snow.css";
import React, { useEffect, useRef, useState } from "react";

import { FONT_BOOK, FONT_HEAVY, FONT_MED } from "../../styles/fonts";

interface Props {
  richTextValue: string;
  smallFont?: boolean;
  maxLines?: number;
}

export default function RenderRichText({
  richTextValue,
  smallFont,
  maxLines,
}: Props) {
  const classes = useStyles();
  const [isExpanded, setIsExpanded] = useState(false);
  const [showReadMore, setShowReadMore] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setIsExpanded(false);
    if (maxLines && contentRef.current) {
      const computedStyle = getComputedStyle(contentRef.current);
      const lineHeight = parseFloat(computedStyle.lineHeight) || 20;
      const maxHeight = lineHeight * maxLines;
      const actualHeight = contentRef.current.scrollHeight;
      setShowReadMore(actualHeight > maxHeight + 5);
    }
  }, [richTextValue, maxLines]);

  const richTextClasses = `${classes.richText} ${maxLines && !isExpanded ? classes.clamped : ""}`;

  return (
    <div className={`ql-snow richText ${classes.container}`}>
      <div className="ql-editor">
        <div
          ref={contentRef}
          dangerouslySetInnerHTML={{ __html: richTextValue }}
          className={richTextClasses}
          style={{
            fontSize: smallFont ? '0.875rem' : '1rem',
            WebkitLineClamp: maxLines && !isExpanded ? maxLines : "unset",
          }}
        />
        {maxLines && showReadMore && (
          <div
            className={classes.readMoreButton}
            style={{
              fontSize: smallFont ? '0.875rem' : '1rem',
              marginTop: 8,
              cursor: "pointer",
            }}
            onClick={() => setIsExpanded(!isExpanded)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                setIsExpanded(!isExpanded);
              }
            }}
            role="button"
            tabIndex={0}
          >
            {isExpanded ? "Read Less" : "Read More"}
          </div>
        )}
      </div>
    </div>
  );
}

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    "& .ql-editor": {
      paddingLeft: 0,
      paddingRight: 0,
    },
  },
  richText: {
    width: "100%",
    overflowWrap: "break-word",
    "& img": {
      maxWidth: "100%",
    },
    "& strong": {
      ...FONT_HEAVY,
    },
    "& b": {
      ...FONT_HEAVY,
    },
    "& em": {
      ...FONT_BOOK,
    },
    "& i": {
      ...FONT_BOOK,
    },
    "& ul": {
      paddingLeft: "1.5em",
      listStyleType: "disc",
    },
    "& ul > li": {
      paddingLeft: 0,
      listStyleType: "disc !important",
    },
    "& ul > li::before": {
      display: "none !important",
    },
  },
  clamped: {
    display: "-webkit-box",
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
  },
  readMoreButton: {
    ...FONT_MED,
    color: theme.palette.primary.main,
    cursor: "pointer",
    "&:hover": {
      textDecoration: "underline",
    },
  },
}));
